import type {TestContext} from './types';

const ARRAY_KEYS = [
    'items', 'account', 'accounts', 'app', 'apps', 'adUnit', 'adUnits',
    'adMobNetworkWaterfallAdUnits',
    'mediationGroups', 'placements', 'records', 'respList', 'adSources',
    'adapters', 'adUnitMappings', 'steps', 'actors', 'operations',
];

const WRAPPER_KEYS = ['data', 'response', 'result', 'resp'];

export function firstArray(value: any): any[] {
    if (Array.isArray(value)) {
        return value;
    }
    const queue: any[] = value && typeof value === 'object' ? [value] : [];
    while (queue.length) {
        const current = queue.shift();
        for (const key of ARRAY_KEYS) {
            if (Array.isArray(current[key])) {
                return current[key];
            }
        }
        for (const key of WRAPPER_KEYS) {
            if (current[key] && typeof current[key] === 'object') {
                queue.push(current[key]);
            }
        }
    }
    return [];
}

export function stripAccount(value: any): string {
    return String(value || '').replace(/^accounts\//, '');
}

export function resourceIdFromName(name: any): string {
    const text = String(name || '');
    const parts = text.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
}

export function getByPath(obj: any, path?: string): any {
    if (!path) {
        return obj;
    }
    return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

function normalizeLineRow(row: any): any {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
        return row;
    }
    const normalized = {...row};
    if (normalized.displayName == null && normalized.display_name != null) {
        normalized.displayName = normalized.display_name;
    }
    if (normalized.adSourceId == null && normalized.ad_source_id != null) {
        normalized.adSourceId = normalized.ad_source_id;
    }
    if (normalized.cpmMicros == null && normalized.cpm_micros != null) {
        normalized.cpmMicros = normalized.cpm_micros;
    }
    return normalized;
}

/** Convert array or string-keyed map payloads into table rows. */
export function mapToRows(value: any): any[] {
    if (Array.isArray(value)) {
        return value.map(normalizeLineRow);
    }
    if (value && typeof value === 'object') {
        return Object.entries(value).map(([key, item]) => {
            if (item && typeof item === 'object' && !Array.isArray(item)) {
                return normalizeLineRow({id: (item as any).id || key, ...item});
            }
            return {id: key, value: item};
        });
    }
    return [];
}

function isMediationGroupShape(data: any): boolean {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return false;
    }
    if (data.mediationGroupId != null) {
        return true;
    }
    const name = String(data.name || '');
    return name.includes('/mediationGroups/');
}

/** Resolve mediationGroupLines from group object, items alias, or bare line map. */
export function resolveMediationGroupLines(data: any): any[] {
    if (!data || typeof data !== 'object') {
        return [];
    }
    const lines =
        data.mediationGroupLines ??
        data.mediation_group_lines ??
        data.items;
    if (lines !== undefined && lines !== null) {
        return mapToRows(lines);
    }
    if (!isMediationGroupShape(data)) {
        return mapToRows(data);
    }
    return [];
}

export function extractMeta(upstream: any, ctx: TestContext): void {
    const meta = upstream?.meta || {};
    if (meta.request_id) {
        ctx.request_id = String(meta.request_id);
    }
    if (meta.operation_id) {
        ctx.operation_id = String(meta.operation_id);
        if (!ctx.audit_operation_id) {
            ctx.audit_operation_id = String(meta.operation_id);
        }
    }
}

export function pickAdmobAccountId(item: any): string {
    if (!item || typeof item !== 'object') {
        return '';
    }
    return String(
        item.account_id ||
            item.accountId ||
            item.publisherId ||
            stripAccount(item.name) ||
            item.id ||
            '',
    );
}

export function setIf(ctx: TestContext, key: string, value: any): void {
    if (value === undefined || value === null || value === '') {
        return;
    }
    ctx[key] = String(value);
}
